import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';

import { Button, Row, Separator, Text } from '@cuemath/comps';
import BallLoader from 'comps/ball-loader';
import { Message } from 'modules/form';
import COLORS from 'constants/colors';
import {
  useConvertClassesToCreditGet,
  useConvertClassesToCreditPatch,
  invalidateConvertClassesToCredit,
} from 'actions/student-course-convert-classes-to-credit';
import { closeModal } from 'actions/modal';
import { getCookie } from 'utils';
// invalidateStudentCoursesGetAll is a direct cache function from @cuemath/rest-api, not a Redux action
import { invalidateStudentCoursesGetAll } from 'actions/studentsv2';

const Wrapper = styled.div`
  padding: 40px 10px 20px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ButtonWrapper = styled(Row)`
  justify-content: center;
`;

const InfoSection = styled.div`
  background-color: ${COLORS.WILD_SAND};
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const InfoRow = styled(Row)`
  justify-content: space-between;
  margin-bottom: 10px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid ${COLORS.GALLERY};
  border-radius: 4px;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: ${COLORS.PRIMARY};
  }
`;

const FeeBreakdownTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 13px;
  & th,
  & td {
    padding: 8px 12px;
    text-align: left;
    border-bottom: 1px solid ${COLORS.GALLERY};
  }
  & th {
    font-weight: 600;
    color: ${COLORS.ABBEY};
    background-color: ${COLORS.HIGHLIGHT};
  }
  & td {
    color: ${COLORS.FONT};
  }
`;

// Sub-component: Fee breakdown table
const FeeBreakdownSection = ({ feeBreakdown, currency }) => {
  if (!feeBreakdown || feeBreakdown.length === 0) return null;

  return (
    <>
      <Text style={{ marginTop: '15px', marginBottom: '5px', fontWeight: 600 }}>
        Fee Breakdown
      </Text>
      <FeeBreakdownTable>
        <thead>
          <tr>
            <th>Classes</th>
            <th>Cost Per Class</th>
            <th>Total</th>
            <th>Type</th>
          </tr>
        </thead>
        <tbody>
          {feeBreakdown.map((fee, idx) => (
            <tr key={idx}>
              <td>{fee.classes_remaining}</td>
              <td>
                {currency} {fee.fee_per_class.toFixed(2)}
              </td>
              <td>
                {currency} {(fee.classes_remaining * fee.fee_per_class).toFixed(2)}
              </td>
              <td>{fee.fee_per_class > 0 ? 'Paid' : 'Free/Referral'}</td>
            </tr>
          ))}
        </tbody>
      </FeeBreakdownTable>
    </>
  );
};

// Sub-component: Confirmation dialog with breakdown
const ConfirmationDialog = ({
  classesCount,
  paidClasses,
  freeClasses,
  currency,
  estimatedAmount,
  loading,
  onCancel,
  onConfirm,
}) => (
  <>
    <div style={{ textAlign: 'center', marginBottom: '20px', lineHeight: 1.8 }}>
      <Text style={{ fontSize: '15px' }}>
        Converting <Text style={{ fontWeight: 700 }}>{classesCount}</Text> class
        {classesCount !== 1 ? 'es' : ''} to credit:
      </Text>
      <Separator transparent height="10" />
      <Text style={{ color: COLORS.ABBEY }}>
        <Text style={{ fontWeight: 700 }}>{paidClasses}</Text> paid class
        {paidClasses !== 1 ? 'es' : ''} →{' '}
        <Text style={{ fontWeight: 700 }}>
          {currency} {estimatedAmount}
        </Text>{' '}
        credit
      </Text>
      {freeClasses > 0 && (
        <>
          <Separator transparent height="5" />
          <Text style={{ color: COLORS.SILVERCHALICE }}>
            <Text style={{ fontWeight: 700 }}>{freeClasses}</Text> free/referral class
            {freeClasses !== 1 ? 'es' : ''} →{' '}
            <Text style={{ fontWeight: 700 }}>{currency} 0.00</Text> credit
          </Text>
        </>
      )}
      <Separator transparent height="15" />
      <Text style={{ color: COLORS.NEGATIVE, fontSize: '13px' }}>
        This action cannot be undone.
      </Text>
    </div>
    <ButtonWrapper>
      {loading ? (
        <BallLoader />
      ) : (
        <>
          <Button align="center" label="Cancel" onClick={onCancel} secondary />
          <Separator transparent width="10" />
          <Button align="center" label="Confirm" onClick={onConfirm} />
        </>
      )}
    </ButtonWrapper>
  </>
);

const StudentConvertClassesToCreditForm = ({ studentCourse, actions, studentId }) => {
  const [classesToTransfer, setClassesToTransfer] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const timerRef = useRef(null);

  const { id: studentCourseId } = studentCourse || {};
  const userId = getCookie('user_id') || getCookie('emp_id') || '';

  // Use new @cuemath/rest-api hooks
  const {
    data: convertDetails,
    get: fetchConversionInfo,
    isProcessing: isGetProcessing,
  } = useConvertClassesToCreditGet(studentCourseId);

  const handlePatchComplete = __ERROR__ => {
    if (__ERROR__) {
      setError(__ERROR__);
      setLoading(false);
    } else {
      setSuccess(true);
      setLoading(false);
      onCloseModal();
    }
  };

  const {
    patch: patchConvertClassesToCredit,
    isProcessing: isPatchProcessing,
  } = useConvertClassesToCreditPatch({
    onComplete: handlePatchComplete,
  });

  const isLoaded = !!convertDetails && !isGetProcessing;

  const {
    pending_classes: pendingClasses,
    unserviced_amount: unservicedAmount,
    currency,
    enrollment_type: enrollmentType,
    classes_balance: classesBalance,
    fee_breakdown: feeBreakdown,
  } = convertDetails || {};

  const currencyLabel = currency || 'INR';

  const onCloseModal = useCallback(() => {
    timerRef.current = setTimeout(() => {
      actions.closeModal();
      if (studentId) {
        invalidateStudentCoursesGetAll(studentId);
        invalidateConvertClassesToCredit(studentCourseId);
      }
    }, 1500);
  }, [actions, studentId, studentCourseId]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (studentCourseId) {
      fetchConversionInfo(studentCourseId);
    }
  }, [studentCourseId, fetchConversionInfo]);

  const handleClassesToTransferChange = e => {
    const value = e.target.value;
    if (value !== '' && !Number.isInteger(Number(value))) return;
    if (value === '' || Number(value) > 0) {
      setClassesToTransfer(value);
      if (Number(value) > pendingClasses) {
        setError(`Cannot convert more than ${pendingClasses} classes`);
      } else {
        setError(null);
      }
    }
  };

  const onConvertClick = () => {
    if (!userId) {
      setError('User ID not found. Please refresh and try again.');
      return;
    }
    setShowConfirmation(true);
  };

  const onConfirm = () => {
    const requestBody = {
      actor_id: userId,
    };

    if (classesToTransfer) {
      const numClasses = parseInt(classesToTransfer, 10);
      if (numClasses > 0 && numClasses <= pendingClasses) {
        requestBody.classes_to_transfer = numClasses;
      } else {
        setError(`Please enter a valid number between 1 and ${pendingClasses}`);
        return;
      }
    }

    setLoading(true);
    setError(null);
    setShowConfirmation(false);
    patchConvertClassesToCredit(studentCourseId, requestBody);
  };

  const onCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  const classesCount = classesToTransfer
    ? parseInt(classesToTransfer, 10)
    : pendingClasses;

  // Calculate exact credit using fee breakdown (FIFO order from backend)
  const calculateExactCredit = numClasses => {
    if (!feeBreakdown || !Array.isArray(feeBreakdown) || feeBreakdown.length === 0) {
      return unservicedAmount ? parseFloat(unservicedAmount).toFixed(2) : '0.00';
    }
    let remaining = numClasses;
    let total = 0;
    for (const fee of feeBreakdown) {
      const take = Math.min(remaining, fee.classes_remaining);
      total += take * fee.fee_per_class;
      remaining -= take;
      if (remaining <= 0) break;
    }
    return total.toFixed(2);
  };
  const estimatedAmount = calculateExactCredit(classesCount);

  // Calculate how many paid vs free classes in the conversion
  const getClassBreakdown = numClasses => {
    if (!feeBreakdown || !Array.isArray(feeBreakdown) || feeBreakdown.length === 0) {
      return { paidClasses: numClasses, freeClasses: 0 };
    }
    let remaining = numClasses;
    let paid = 0;
    let free = 0;
    for (const fee of feeBreakdown) {
      const take = Math.min(remaining, fee.classes_remaining);
      if (fee.fee_per_class > 0) {
        paid += take;
      } else {
        free += take;
      }
      remaining -= take;
      if (remaining <= 0) break;
    }
    return { paidClasses: paid, freeClasses: free };
  };
  const { paidClasses, freeClasses } = getClassBreakdown(classesCount);

  // Compute totals from fee breakdown
  const totalPaidClasses = feeBreakdown
    ? feeBreakdown
        .filter(f => f.fee_per_class > 0)
        .reduce((s, f) => s + f.classes_remaining, 0)
    : 0;
  const totalFreeClasses = feeBreakdown
    ? feeBreakdown
        .filter(f => f.fee_per_class === 0)
        .reduce((s, f) => s + f.classes_remaining, 0)
    : 0;

  const isButtonDisabled =
    loading ||
    success ||
    !isLoaded ||
    pendingClasses === 0 ||
    (classesToTransfer && Number(classesToTransfer) > pendingClasses);

  const showLoading = isGetProcessing && !convertDetails;

  return (
    <Wrapper>
      {showLoading && <BallLoader />}

      {isLoaded && (
        <>
          <InfoSection>
            <Text style={{ fontWeight: 600, fontSize: '16px', marginBottom: '15px' }}>
              Course Information
            </Text>
            <InfoRow>
              <Text style={{ fontWeight: 600 }}>Pending Classes:</Text>
              <Text style={{ color: COLORS.GRAY }}>
                {pendingClasses || 0}
                {totalPaidClasses > 0 && totalFreeClasses > 0 && (
                  <Text style={{ color: COLORS.GRAY }}>
                    {' '}
                    ({totalPaidClasses} paid, {totalFreeClasses} free/referral)
                  </Text>
                )}
              </Text>
            </InfoRow>
            <InfoRow>
              <Text style={{ fontWeight: 600 }}>Unserviced Amount:</Text>
              <Text style={{ color: COLORS.GRAY }}>
                {currencyLabel}{' '}
                {unservicedAmount ? parseFloat(unservicedAmount).toFixed(2) : '0.00'}
              </Text>
            </InfoRow>
            <InfoRow>
              <Text style={{ fontWeight: 600 }}>Current Classes Balance:</Text>
              <Text style={{ color: COLORS.GRAY }}>{classesBalance || 0}</Text>
            </InfoRow>
            <InfoRow>
              <Text style={{ fontWeight: 600 }}>Enrollment Type:</Text>
              <Text style={{ color: COLORS.GRAY }}>{enrollmentType || '-'}</Text>
            </InfoRow>
            <FeeBreakdownSection feeBreakdown={feeBreakdown} currency={currencyLabel} />
          </InfoSection>

          {pendingClasses > 0 && !showConfirmation && (
            <div style={{ marginBottom: '20px' }}>
              <Text
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 500,
                  color: COLORS.ABBEY,
                }}
              >
                Classes to Convert (Optional):
              </Text>
              <Input
                id="classes_to_transfer"
                type="number"
                min="1"
                max={pendingClasses}
                step={1}
                value={classesToTransfer}
                onChange={handleClassesToTransferChange}
                placeholder={`Leave empty to convert all ${pendingClasses} classes`}
                disabled={loading || success}
              />
              <Text
                style={{
                  display: 'block',
                  marginTop: '5px',
                  fontSize: '12px',
                  color: COLORS.SILVERCHALICE,
                }}
              >
                If not specified, all {pendingClasses} remaining classes will be converted
                to credit
              </Text>
            </div>
          )}

          {pendingClasses === 0 && (
            <Message width="100%" error>
              No pending classes available to convert
            </Message>
          )}
        </>
      )}

      {error && (
        <Message width="100%" error>
          {error}
        </Message>
      )}

      {success ? (
        <Message width="100%">
          {successMessage || 'Classes converted to credit successfully'}
        </Message>
      ) : (
        isLoaded && (
          <>
            <Separator transparent height="20" />
            {showConfirmation ? (
              <ConfirmationDialog
                classesCount={classesCount}
                paidClasses={paidClasses}
                freeClasses={freeClasses}
                currency={currencyLabel}
                estimatedAmount={estimatedAmount}
                loading={loading}
                onCancel={onCancelConfirmation}
                onConfirm={onConfirm}
              />
            ) : (
              <ButtonWrapper>
                <Button
                  align="center"
                  label="Convert Classes to Credit"
                  onClick={onConvertClick}
                  disabled={isButtonDisabled}
                />
              </ButtonWrapper>
            )}
          </>
        )
      )}
    </Wrapper>
  );
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ closeModal }, dispatch),
});

export default connect(null, mapDispatchToProps)(StudentConvertClassesToCreditForm);
